import { TOKEN_KEY } from '@/config/constants'

const ACTIVITY_KEY = 'yuyuan-script-activity'
let getRoute = () => null

export function setScriptActivityRouteProvider(provider) {
  getRoute = provider
}

// POST is also used for reads. Only actual creative-tool writes belong here.
export function isScriptMutation(method, path) {
  const verb = String(method || 'get').toUpperCase()
  if (!/^\/api\/(?:video|workspace|canvas|music|voice|volc\/asset|script-assets|scripts)(?:\/|$)/.test(path)) return false
  if (/^\/api\/scripts\/\d+\/image-cover$/.test(path)) return false // Automatic cover initialization is not an edit.
  if (['PUT', 'PATCH', 'DELETE'].includes(verb)) return true
  if (verb !== 'POST') return false
  return [
    /^\/api\/video\/(?:generate|image\/(?:generate|retry)|generation\/accept|remix(?:\/[^/]+)?)$/,
    /^\/api\/video\/(?:image\/records\/[^/]+\/versions|records\/[^/]+\/subtitle-removal)$/,
    /^\/api\/video\/subtitle-removal\/tasks(?:\/[^/]+\/(?:resume-query|result-url))?$/,
    /^\/api\/workspace$/,
    /^\/api\/canvas\/(?:projects|canvases|canvases\/[^/]+\/(?:revisions|revisions\/[^/]+\/restore|generation-tasks|video-edit-tasks))$/,
    /^\/api\/music\/(?:tasks|sounds)$/,
    /^\/api\/voice\/(?:generate(?:\/delete)?|voices\/(?:clone|delete))$/,
    /^\/api\/volc\/asset\/(?:group\/create|upload(?:\/batch)?)$/,
    /^\/api\/script-assets\/\d+\/(?:extract|generate|import-assets|identity-reference)$/,
    /^\/api\/scripts(?:\/\d+\/opened)?$/
  ].some(pattern => pattern.test(path))
}

function association(value) {
  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    return value.has('scriptId') ? { present: true, value: value.get('scriptId') } : { present: false }
  }
  return value && Object.prototype.hasOwnProperty.call(value, 'scriptId')
    ? { present: true, value: value.scriptId } : { present: false }
}

export function attachScriptActivity(config, route = getRoute()) {
  const url = new URL(config.url, config.baseURL || 'http://localhost')
  if (!isScriptMutation(config.method, url.pathname)) return config
  const pathId = url.pathname.match(/^\/api\/(?:scripts|script-assets)\/(\d+)(?:\/|$)/)?.[1]
  const explicit = [association(config.data), association(config.params),
    { present: url.searchParams.has('scriptId'), value: url.searchParams.get('scriptId') }].find(item => item.present)
  // Never overwrite an explicit association (including null), or use a globally
  // selected script from another tab. Queued requests keep their own scriptId.
  const scriptId = pathId ?? (explicit ? explicit.value : route?.params?.scriptId ?? route?.query?.scriptId)
  if (!/^\d+$/.test(String(scriptId ?? '')) || Number(scriptId) <= 0) return config
  return { ...config, params: { ...config.params, scriptId: String(scriptId) }, scriptActivityId: String(scriptId) }
}

export function publishScriptActivity(response, browser = window) {
  const config = response.config
  if (response.data?.code !== 200 || !config?.scriptActivityId) return
  const token = browser.localStorage.getItem(TOKEN_KEY)
  if (!token || token.replace(/^Bearer /, '') !== config.__authToken) return
  const change = { scriptId: config.scriptActivityId, sessionToken: token, nonce: `${Date.now()}-${Math.random()}` }
  try { browser.localStorage.setItem(ACTIVITY_KEY, JSON.stringify(change)) } catch (_) { /* Local events still work. */ }
  browser.dispatchEvent(new browser.CustomEvent(ACTIVITY_KEY, { detail: change }))
}

export function subscribeScriptActivity(callback, browser = window) {
  const receive = change => {
    if (change?.scriptId && change.sessionToken && change.sessionToken === browser.localStorage.getItem(TOKEN_KEY)) callback(change.scriptId)
  }
  const storage = event => {
    if (event.key !== ACTIVITY_KEY || event.storageArea !== browser.localStorage) return
    try { receive(JSON.parse(event.newValue)) } catch (_) { /* Ignore incomplete storage data. */ }
  }
  const local = event => receive(event.detail)
  browser.addEventListener('storage', storage)
  browser.addEventListener(ACTIVITY_KEY, local)
  return () => {
    browser.removeEventListener('storage', storage)
    browser.removeEventListener(ACTIVITY_KEY, local)
  }
}

function scriptTime(script) {
  for (const value of [script.userLastModifiedAt, script.createdAt]) {
    if (value == null || value === '') continue
    const time = typeof value === 'number' ? value : Date.parse(String(value).replace(' ', 'T'))
    if (Number.isFinite(time)) return time
  }
  return 0
}

export function compareScriptModification(a, b) {
  return scriptTime(b) - scriptTime(a)
    || String(b.id).localeCompare(String(a.id), undefined, { numeric: true })
}
